import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Loader2, Send, Mail, MessageSquare, Plus, X, CalendarClock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const money = (v) => `${v < 0 ? '−' : ''}£${Math.abs(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

function RecipientList({ label, icon: Icon, items, onChange, placeholder, canEdit, testid }) {
  const [draft, setDraft] = useState('');
  const add = () => { const v = draft.trim(); if (!v) return; onChange([...items, v]); setDraft(''); };
  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1 mb-2"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <ul className="flex flex-wrap gap-2 mb-2">
        {items.map((v) => (
          <li key={v} className="inline-flex items-center gap-1 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1" data-testid={`${testid}-item`}>
            {v}
            {canEdit && <button type="button" onClick={() => onChange(items.filter((x) => x !== v))} className="text-amber-700 hover:text-rose-600" aria-label={`Remove ${v}`}><X className="w-3.5 h-3.5" /></button>}
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-gray-400">Nobody yet</li>}
      </ul>
      {canEdit && (
        <div className="flex gap-2 max-w-sm">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} placeholder={placeholder} className="h-9" data-testid={`${testid}-input`} />
          <Button type="button" variant="outline" size="sm" onClick={add} className="h-9" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}

export function WeeklyDigest() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [rem, setRem] = useState(null);
  const [remBusy, setRemBusy] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/api/staff/profit-centre/cost-reminder`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then(setRem).catch(() => {});
  }, []);
  const toggleRem = async () => {
    const r = await fetch(`${API_URL}/api/staff/profit-centre/cost-reminder`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !rem.enabled }) });
    if (r.ok) setRem({ ...rem, ...(await r.json()) }); else toast.error('Could not update');
  };
  const remNow = async () => {
    if (!window.confirm('Text the uncosted-orders reminder to everyone on the text list now?')) return;
    setRemBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/profit-centre/cost-reminder/send-now`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      toast.success(`Reminder texted to ${Object.keys(d.sms || {}).length} number(s)`);
    } catch (e) { toast.error(e.message); } finally { setRemBusy(false); }
  };

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      fetch(`${API_URL}/api/staff/profit-centre/weekly-digest`, { credentials: 'include' }),
      fetch(`${API_URL}/api/staff/profit-centre/weekly-digest/preview`, { credentials: 'include' }),
    ]);
    if (a.ok) setCfg(await a.json());
    if (b.ok) setPreview(await b.json());
  }, []);
  useEffect(() => { load().catch(() => toast.error('Could not load weekly digest')); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/profit-centre/weekly-digest`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: next.enabled, monthly_enabled: next.monthly_enabled !== false, daily_enabled: next.daily_enabled !== false, daily_hour: next.daily_hour ?? 17, weekly_day: next.weekly_day ?? 4, weekly_hour: next.weekly_hour ?? 17, monthly_hour: next.monthly_hour ?? 7, sms_numbers: next.sms_numbers, emails: next.emails }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setCfg((c) => ({ ...c, ...d }));
      toast.success('Schedule updated');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const sendNow = async (period = 'week') => {
    if (!window.confirm(`Send ${period === 'month' ? "last month's" : "this week's"} profit text + email to everyone listed right now?`)) return;
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/profit-centre/weekly-digest/send-now`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      const fails = [...Object.entries(d.sms || {}), ...Object.entries(d.email || {})].filter(([, v]) => String(v).startsWith('failed'));
      if (fails.length) toast.warning(`Sent with ${fails.length} failure(s)`, { description: fails.map(([k, v]) => `${k}: ${v}`).join(' · ').slice(0, 300) });
      else toast.success(period === 'month' ? 'Monthly profit digest sent' : 'Weekly profit digest sent');
      load();
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  if (!cfg) return <Card className="p-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></Card>;
  const HOURS = Array.from({ length: 24 }, (_, h) => h);
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const sel = 'h-8 text-xs border border-gray-300 rounded-md px-1.5 bg-white disabled:opacity-60';
  const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'am' : 'pm'}`;
  const TimePick = ({ hourKey, dayKey, def }) => (
    <div className="inline-flex flex-wrap items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-3 py-2" data-testid={`pc-time-${hourKey}`}>
      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Send time</span>
      {dayKey && <select value={cfg[dayKey] ?? 4} disabled={!isAdmin || saving} onChange={(e) => save({ ...cfg, [dayKey]: Number(e.target.value) })} className={sel} data-testid={`pc-day-${dayKey}`}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select>}
      <select value={cfg[hourKey] ?? def} disabled={!isAdmin || saving} onChange={(e) => save({ ...cfg, [hourKey]: Number(e.target.value) })} className={`${sel} font-bold text-sm h-9`} data-testid={`pc-hour-${hourKey}`}>{HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}</select>
      <span className="text-xs text-gray-500">UK time{saving ? ' · saving…' : ' · saves instantly'}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="pc-weekly-digest">
      <Card className="p-4 flex flex-wrap items-center gap-4" data-testid="pc-daily-digest">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold text-gray-900">Daily profit text + email + push</div>
          <p className="text-xs text-gray-500">{cfg.daily_schedule || 'Every day 19:00 (Europe/London)'}. Today's ProBound, ProEpoxy and ProDecor sales, cost and profit plus the total, ex VAT. No training figures.</p>
          {cfg.daily_last_sent_at && <p className="text-xs text-emerald-700 mt-0.5" data-testid="pc-daily-last-sent">Last sent {fmt(cfg.daily_last_sent_at)}</p>}
          <div className="mt-2"><TimePick hourKey="daily_hour" def={19} /></div>
        </div>
        <button type="button" role="switch" aria-checked={cfg.daily_enabled !== false} disabled={!isAdmin || saving} onClick={() => save({ ...cfg, daily_enabled: cfg.daily_enabled === false })}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border ${cfg.daily_enabled !== false ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`} data-testid="pc-daily-toggle">
          {cfg.daily_enabled !== false ? 'Scheduled · ON' : 'Paused'}
        </button>
        {isAdmin && (
          <Button size="sm" onClick={() => sendNow('day')} disabled={sending} className="bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="pc-daily-send-now">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send today's now
          </Button>
        )}
      </Card>

      <Card className="p-4" data-testid="pc-digest-recipients">
        <div className="font-bold text-gray-900 text-sm">Who receives the profit texts</div>
        <p className="text-xs text-gray-500 mb-2">Everyone with Profit Centre access (admins, plus anyone an admin has granted access in Staff → Users) gets the text, email and push — on top of the fixed numbers below.</p>
        <div className="flex flex-wrap gap-2">
          {(cfg.profit_access_recipients || []).map((u) => <span key={u.email} className="text-xs border rounded-full px-2.5 py-1 bg-gray-50" data-testid="pc-recipient"><b>{u.name}</b> · {u.phone || <span className="text-amber-700">no mobile saved</span>} · {u.email}</span>)}
        </div>
        {cfg.last_cron_runs && (
          <p className="text-[11px] text-gray-500 mt-3" data-testid="pc-cron-runs">Last automatic runs — daily: {fmt(cfg.last_cron_runs.profit_day) || 'not yet'} · weekly: {fmt(cfg.last_cron_runs.profit_week) || 'not yet'} · monthly: {fmt(cfg.last_cron_runs.profit_month) || 'not yet'}. If a scheduled send is missing here, the scheduler didn't fire — contact support. If it ran but no text arrived, check the Firetext balance and the mobile numbers.</p>
        )}
      </Card>

      <Card className="p-4 flex flex-wrap items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold text-gray-900">Weekly profit text + email</div>
          <p className="text-xs text-gray-500">{cfg.schedule}. Covers the week since the previous send: ProBound, ProEpoxy, ProDecor (plus any unbranded sales log entries) and a total — all ex VAT. Training is only in the monthly report.</p>
          {cfg.last_sent_at && <p className="text-xs text-emerald-700 mt-0.5" data-testid="pc-digest-last-sent">Last sent {fmt(cfg.last_sent_at)}</p>}
          <div className="mt-1.5"><TimePick hourKey="weekly_hour" dayKey="weekly_day" def={17} /></div>
        </div>
        <button type="button" role="switch" aria-checked={!!cfg.enabled} disabled={!isAdmin || saving} onClick={() => save({ ...cfg, enabled: !cfg.enabled })}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border ${cfg.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`} data-testid="pc-digest-toggle">
          {cfg.enabled ? 'Scheduled · ON' : 'Paused'}
        </button>
        {isAdmin && (
          <Button size="sm" onClick={() => sendNow('week')} disabled={sending} className="bg-amber-700 hover:bg-amber-600 text-white" data-testid="pc-digest-send-now">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send now
          </Button>
        )}
      </Card>

      <Card className="p-4 flex flex-wrap items-center gap-4" data-testid="pc-monthly-digest">
        <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-800 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-bold text-gray-900">Monthly profit text + email</div>
          <p className="text-xs text-gray-500">{cfg.monthly_schedule || '1st of every month, 8am'}. Full report — Training, ProBound, ProEpoxy, ProDecor and Other — covering the whole previous calendar month, same recipients.</p>
          {cfg.monthly_last_sent_at && <p className="text-xs text-emerald-700 mt-0.5" data-testid="pc-monthly-last-sent">Last sent {fmt(cfg.monthly_last_sent_at)}</p>}
          <div className="mt-1.5"><TimePick hourKey="monthly_hour" def={7} /></div>
        </div>
        <button type="button" role="switch" aria-checked={cfg.monthly_enabled !== false} disabled={!isAdmin || saving} onClick={() => save({ ...cfg, monthly_enabled: cfg.monthly_enabled === false })}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border ${cfg.monthly_enabled !== false ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`} data-testid="pc-monthly-toggle">
          {cfg.monthly_enabled !== false ? 'Scheduled · ON' : 'Paused'}
        </button>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={() => sendNow('month')} disabled={sending} data-testid="pc-monthly-send-now">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send last month now
          </Button>
        )}
      </Card>

      {rem && (
        <Card className="p-4 flex flex-wrap items-center gap-4" data-testid="pc-cost-reminder">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${rem.report.count ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}><AlertTriangle className="w-5 h-5" /></div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-bold text-gray-900">Thursday cost reminder text</div>
            <p className="text-xs text-gray-500">{rem.schedule}. Texts the same numbers a list of this week's orders with no cost logged, so Friday's profit is right.</p>
            <p className="text-xs mt-0.5 font-semibold" data-testid="pc-cost-reminder-count">
              {rem.report.count ? <span className="text-rose-700">{rem.report.count} order{rem.report.count === 1 ? '' : 's'} uncosted right now · {money(rem.report.revenue)} of sales</span> : <span className="text-emerald-700">Everything this week has a cost</span>}
            </p>
          </div>
          <button type="button" role="switch" aria-checked={!!rem.enabled} disabled={!isAdmin} onClick={toggleRem}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border ${rem.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`} data-testid="pc-cost-reminder-toggle">
            {rem.enabled ? 'Scheduled · ON' : 'Paused'}
          </button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={remNow} disabled={remBusy} data-testid="pc-cost-reminder-send-now">
              {remBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />} Text reminder now
            </Button>
          )}
        </Card>
      )}

      <Card className="p-4 grid md:grid-cols-2 gap-6">
        <RecipientList label="Text to" icon={MessageSquare} items={cfg.sms_numbers || []} canEdit={isAdmin} placeholder="07xxx xxxxxx" testid="pc-digest-sms"
          onChange={(v) => save({ ...cfg, sms_numbers: v })} />
        <RecipientList label="Email to" icon={Mail} items={cfg.emails || []} canEdit={isAdmin} placeholder="name@oxresin.co.uk" testid="pc-digest-email"
          onChange={(v) => save({ ...cfg, emails: v })} />
      </Card>

      {preview && (
        <Card className="p-4 space-y-3" data-testid="pc-digest-preview">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Preview — what would go out next Friday</div>
            <div className="text-xs text-gray-500">{fmt(preview.report.start)} → {fmt(preview.report.end)}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {preview.report.sections.filter((s) => s.key !== 'other' || s.count || s.cost).map((s) => (
              <div key={s.key} className="rounded-lg border p-3 bg-white" data-testid={`pc-digest-section-${s.key}`}>
                <div className="text-xs font-semibold text-gray-500">{s.label}</div>
                <div className={`text-lg font-bold ${s.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(s.profit)}</div>
                <div className="text-[11px] text-gray-500">sales {money(s.revenue)} · cost {money(s.cost)}</div>
                {s.uncosted > 0 && <div className="text-[11px] text-amber-700">{s.uncosted} uncosted</div>}
              </div>
            ))}
            <div className="rounded-lg border-2 border-gray-900 p-3 bg-gray-900 text-white" data-testid="pc-digest-total">
              <div className="text-xs font-semibold text-white/70">Total</div>
              <div className="text-lg font-bold">{money(preview.report.total.profit)}</div>
              <div className="text-[11px] text-white/70">sales {money(preview.report.total.revenue)} · cost {money(preview.report.total.cost)}</div>
            </div>
          </div>
          <pre className="text-xs bg-gray-50 border rounded-lg p-3 whitespace-pre-wrap font-mono text-gray-700" data-testid="pc-digest-sms-preview">{preview.sms}</pre>
        </Card>
      )}
    </div>
  );
}
